(() => {
    'use strict';

    const reducedMotionQuery = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
    );

    const sectionInstances = new WeakMap();

    const isDesignMode = () =>
        Boolean(
            window.Shopify &&
            window.Shopify.designMode
        );

    const revealAll = (root = document) => {
        const elements = [];

        if (
            root instanceof Element &&
            root.matches('[data-reveal]')
        ) {
            elements.push(root);
        }

        elements.push(
            ...root.querySelectorAll('[data-reveal]')
        );

        elements.forEach((element) => {
            element.classList.add('is-revealed');
        });
    };

    const syncEnhancementClass = () => {
        const enabled =
            !reducedMotionQuery.matches &&
            !isDesignMode();

        document.documentElement.classList.toggle(
            'theme-reveal-enabled',
            enabled
        );

        if (!enabled) {
            revealAll(document);
        }
    };

    class RevealController {
        constructor(root = document) {
            this.root = root;
            this.observer = null;

            this.init();
        }

        getElements() {
            const elements = [];

            if (
                this.root instanceof Element &&
                this.root.matches(
                    '[data-reveal]:not(.is-revealed)'
                )
            ) {
                elements.push(this.root);
            }

            elements.push(
                ...this.root.querySelectorAll(
                    '[data-reveal]:not(.is-revealed)'
                )
            );

            return elements;
        }

        init() {
            const elements = this.getElements();

            if (!elements.length) {
                return;
            }

            /*
             * Reveal immediately when animation should not run:
             * - reduced motion
             * - Shopify Theme Editor
             * - browsers without IntersectionObserver
             */
            if (
                reducedMotionQuery.matches ||
                isDesignMode() ||
                !('IntersectionObserver' in window)
            ) {
                elements.forEach((element) => {
                    element.classList.add('is-revealed');
                });

                return;
            }

            this.observer = new IntersectionObserver(
                (entries, observer) => {
                    entries.forEach((entry) => {
                        if (!entry.isIntersecting) {
                            return;
                        }

                        entry.target.classList.add(
                            'is-revealed'
                        );

                        observer.unobserve(
                            entry.target
                        );
                    });
                },
                {
                    threshold: 0.2,
                    rootMargin: '0px 0px -10% 0px',
                }
            );

            elements.forEach((element) => {
                this.observer.observe(element);
            });
        }

        destroy() {
            this.observer?.disconnect();
            this.observer = null;
        }
    }

    const initializeReveal = (
        root = document
    ) => {
        sectionInstances.get(root)?.destroy();

        const instance = new RevealController(
            root
        );

        sectionInstances.set(
            root,
            instance
        );

        return instance;
    };

    const destroyReveal = (root) => {
        const instance =
            sectionInstances.get(root);

        if (!instance) {
            return;
        }

        instance.destroy();
        sectionInstances.delete(root);
    };

    const initialize = () => {
        syncEnhancementClass();
        initializeReveal(document);
    };

    if (document.readyState === 'loading') {
        document.addEventListener(
            'DOMContentLoaded',
            initialize,
            {
                once: true,
            }
        );
    } else {
        initialize();
    }

    /*
     * React if the customer's reduced-motion preference changes
     * while the storefront is open.
     */
    reducedMotionQuery.addEventListener?.(
        'change',
        () => {
            syncEnhancementClass();

            if (reducedMotionQuery.matches) {
                sectionInstances
                    .get(document)
                    ?.destroy();

                revealAll(document);
                return;
            }

            initializeReveal(document);
        }
    );

    /*
     * Shopify Theme Editor:
     * initialize newly rendered sections.
     */
    document.addEventListener(
        'shopify:section:load',
        (event) => {
            syncEnhancementClass();
            initializeReveal(event.target);
        }
    );

    /*
     * Shopify Theme Editor:
     * clean up observers when sections are unloaded.
     */
    document.addEventListener(
        'shopify:section:unload',
        (event) => {
            destroyReveal(event.target);
        }
    );
})();